import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  FormGroup,
  Typography,
  Box,
  CircularProgress
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import InputDropdown from "../../../components/InputFields/InputDropdown";
import InputText from "../../../components/InputFields/InputText";
import { handleAuth } from "../../../components/Login/Auth";
import { getItem } from "../../../utils/storage";
import sendToast from "../../../components/Toast/sendToast";
import AxiosInstance from "../../AxiosInstance";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
} from "reactstrap";
import "../Invoice/style.css";
import { Grid } from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { LoaderComponent } from "../../../components/Icon/Index";
import showToast from "../../../components/Toast/Toster";
import WhiteButton from "../../../components/Button/WhiteButton";
import BlueButton from "../../../components/Button/BlueButton";
import { getGatewayForPaymentMethod } from "../../../utils/gatewayHelper";
import CardPayment from "../Invoice/Payment/CardPayment";
import AchPayment from "../Invoice/Payment/AchPayment";
import { CleaningServicesOutlined, X } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

const InvoicePayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [invoiceData, setInvoiceData] = useState();

  const [buttonLoader, setButtonLoader] = useState(false);

  const [error, setError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const paymentFormik = useFormik({
    initialValues: {
      method: "Cash",
      amount: invoiceData?.invoiceAccount,
      date: dayjs(new Date()).format("MM/DD/YYYY"),
      customer_vault_id: "",
      billing_id: "",
      cheque_number: "",
    },
    validationSchema: Yup.object({
      method: Yup.string().required("This field is required."),
      cheque_number: Yup.string().test(
        "conditional-required",
        "This field is required.",
        function (value) {
          const { method } = this.parent;
          return method !== "Check" || (value && value?.trim().length > 0);
        }
      ),
      amount: Yup.number()
        .required("This field is required.")
        .min(1, "The amount must be at least 1.")
        .max(
          invoiceData?.invoiceAccount,
          `The amount must be less than or equal to ${invoiceData?.invoiceAccount}.`
        ),
    }),
    onSubmit: async (values) => {
      setButtonLoader(true);
      if (!values?.date) {
        setError("Please select date");
      } else {
        if (paymentMethod === "Credit/debit card") {
          const cardGateway = getGatewayForPaymentMethod(companyData, "Credit/debit card");
          const isDejavoo = (cardGateway || "").toString().toUpperCase() === "DEJAVOO";
          const hasCard = isDejavoo
            ? (values?.token || selectedCard?.token)
            : (values?.billing_id || selectedCard);
          if (!hasCard) {
            setError("Please select card");
            setButtonLoader(false);
            return;
          }
          setError("");

          try {
            const cardBillingId = values?.billing_id != null && typeof values.billing_id === "string" && values.billing_id.trim() !== "" && !/^\d+$/.test(String(values.billing_id).trim()) ? values.billing_id : Number(values?.billing_id);
            const cardVaultId = values?.customer_vault_id != null && typeof values.customer_vault_id === "string" && values.customer_vault_id.trim() !== "" && !/^\d+$/.test(String(values.customer_vault_id).trim()) ? values.customer_vault_id : Number(values?.customer_vault_id);
            const cardToken = values?.token ?? selectedCard?.token;
            const paymentData = {
              date: values?.date,
              company: CompanyUrl,
              amount: Number(values?.amount),
              CompanyId: getItem("CompanyId"),
              Total: invoiceData?.Total,
              CustomerId: invoiceData?.CustomerId,
              InvoiceId: invoiceData?.InvoiceId,
              InvoiceNumber: invoiceData?.InvoiceNumber,
              method: "Card",
              paymentMethod: "card_on_file",
              paymentDoingFor: "Invoice",
              paymentSource: "Invoice Payment",
              paymentType: "card_on_file",
              Gateway: cardGateway || companyData?.Gateway,
              billing_id: cardBillingId,
              customer_vault_id: cardVaultId,
            };
            if (isDejavoo && cardToken) {
              paymentData.token = cardToken;
              paymentData.paymentToken = cardToken;
            }
            const object = { paymentData };

            const res = await AxiosInstance.post(
              "/v1/payment/process-payment",
              object
            );
            if (res?.data?.success === true) {
              showToast.success(res?.data?.message || res?.data?.data?.responseMessage || res?.data?.data?.responsetext || "");

              fetchData();
            } else {
              const msg = res?.data?.message || res?.data?.data?.message || res?.data?.data?.responsetext || res?.data?.error || "";
              sendToast(msg || "");
            }
          } catch (error) {
            const errMsg = error?.response?.data?.message || error?.response?.data?.data?.message || error?.response?.data?.data?.responsetext || error?.response?.data?.error || "";
            sendToast(errMsg || "");
          }
        } else if (paymentMethod === "ACH bank payment") {
          if (!values?.billing_id) {
            setError("Please select ACH account");
            setButtonLoader(false);
            return;
          } else {
            setError("");
          }
          try {
            const achBillingId = values?.billing_id != null && typeof values.billing_id === "string" && values.billing_id.trim() !== "" && !/^\d+$/.test(String(values.billing_id).trim()) ? values.billing_id : Number(values?.billing_id);
            const achVaultId = values?.customer_vault_id != null && typeof values.customer_vault_id === "string" && values.customer_vault_id.trim() !== "" && !/^\d+$/.test(String(values.customer_vault_id).trim()) ? values.customer_vault_id : Number(values?.customer_vault_id);
            const achGateway = getGatewayForPaymentMethod(companyData, "ACH bank payment");
            const object = {
              paymentData: {
                billing_id: achBillingId,
                customer_vault_id: achVaultId,
                date: values?.date,
                company: CompanyUrl,
                amount: values?.amount,
                CompanyId: getItem("CompanyId"),
                Total: invoiceData?.Total,
                CustomerId: invoiceData?.CustomerId,
                InvoiceId: invoiceData?.InvoiceId,
                InvoiceNumber: invoiceData?.InvoiceNumber,
                method: "ACH",
                paymentMethod: "ach",
                paymentDoingFor: "Invoice",
                paymentSource: "Invoice Payment",
                paymentType: "Ach",
                Gateway: achGateway || companyData?.Gateway,
              },
            };

            const res = await AxiosInstance.post(
              "/v1/payment/process-payment",
              object
            );

            if (res?.data?.success === true) {
              showToast.success(res?.data?.message || res?.data?.data?.responseMessage || res?.data?.data?.responsetext || "");

              fetchData();
            } else {
              const msg = res?.data?.message || res?.data?.data?.message || res?.data?.data?.responsetext || res?.data?.error || "";
              sendToast(msg || "");
            }
          } catch (error) {
            const errMsg = error?.response?.data?.message || error?.response?.data?.data?.message || error?.response?.data?.data?.responsetext || error?.response?.data?.error || "";
            sendToast(errMsg || "");
          }
        } else {
          try {
            if (paymentFormik?.values?.method === "Cash") {
              var object = {
                paymentData: {
                  date: values?.date,
                  company: CompanyUrl,
                  amount: values?.amount,
                  CompanyId: getItem("CompanyId"),
                  Total: invoiceData?.Total,
                  CustomerId: invoiceData?.CustomerId,
                  InvoiceId: invoiceData?.InvoiceId,
                  InvoiceNumber: invoiceData?.InvoiceNumber,
                  method: "Cash",
                  paymentMethod:"cash",
                  paymentDoingFor:"Invoice",
                  paymentSource: "Invoice Payment",
                  paymentType:"cash",
                },
              };
            } else {
              object = {
                paymentData: {
                  date: values?.date,
                  cheque_number: values?.cheque_number,
                  company: CompanyUrl,
                  amount: values?.amount,
                  CompanyId: getItem("CompanyId"),
                  Total: invoiceData?.Total,
                  CustomerId: invoiceData?.CustomerId,
                  InvoiceId: invoiceData?.InvoiceId,
                  InvoiceNumber: invoiceData?.InvoiceNumber,
                  method: "Check",
                  paymentMethod:"check",
                  paymentDoingFor:"Invoice",
                  paymentSource: "Invoice Payment",
                  paymentType:"check",
                },
              };
            }

            const res = await AxiosInstance.post(
              "/v1/payment/process-payment",
              object
            );

            if (res?.data?.success === true) {
              showToast.success(res?.data?.message || res?.data?.data?.responseMessage || res?.data?.data?.responsetext || "");

              fetchData();
            } else {
              const msg = res?.data?.message || res?.data?.data?.message || res?.data?.data?.responsetext || res?.data?.error || "";
              sendToast(msg || "");
            }
          } catch (error) {
            const errMsg = error?.response?.data?.message || error?.response?.data?.data?.message || error?.response?.data?.data?.responsetext || error?.response?.data?.error || "";
            sendToast(errMsg || "");
          }
        }
      }
      setButtonLoader(false);
    },
  });

  const [selectedCard, setSelectedCard] = useState(undefined);
  const [companyData, setCompanyData] = useState(undefined);
  const [loader, setLoader] = useState(true);

  const gateway = useMemo(
    () => getGatewayForPaymentMethod(companyData, paymentMethod),
    [companyData, paymentMethod]
  );

  // Flags from token decode (gateway-keys IsDelete: false): disable (not hide) Card/ACH when false
  const creditCardEnabled = companyData?.creditCardEnabled !== false;
  const achEnabled = companyData?.achEnabled === true;

  const paymentMethods = useMemo(() => {
    return ["Cash", "ACH bank payment", "Check", "Credit/debit card"];
  }, []);

  const getPaymentMethodDisabled = (option) => {
    if (option === "Credit/debit card" && !creditCardEnabled) return true;
    if (option === "ACH bank payment" && !achEnabled) return true;
    return false;
  };

  const invoiceId = "1737637905702";

  const fetchData = async () => {
    try {
      const res = await handleAuth(navigate, location);
      setCompanyData(res?.data);
      if (invoiceId) {
        const invoiceRes = await AxiosInstance.get(
          `/v1/invoice-payment/${invoiceId}`
        );
        if (invoiceRes?.status === 200) {
          setInvoiceData(invoiceRes?.data?.invoiceData);
          paymentFormik.setFieldValue(
            "amount",
            invoiceRes?.data?.invoiceData?.invoiceAccount
          );
        } else {
        }
      }
    } catch (error) {
      console.error("Error:", error?.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // When companyData loads, if current payment method is hidden by flags, switch to first available
  useEffect(() => {
    if (!companyData?.CompanyId) return;
    const currentHidden =
      (paymentMethod === "Credit/debit card" && !creditCardEnabled) ||
      (paymentMethod === "ACH bank payment" && !achEnabled);
    if (!currentHidden) return;
    const firstAvailable = creditCardEnabled
      ? "Credit/debit card"
      : achEnabled
        ? "ACH bank payment"
        : "Cash";
    setPaymentMethod(firstAvailable);
    paymentFormik.setFieldValue("method", firstAvailable);
    setSelectedCard(undefined);
    paymentFormik.setFieldValue("billing_id", "");
    paymentFormik.setFieldValue("customer_vault_id", "");
    paymentFormik.setFieldValue("token", "");
  }, [companyData?.CompanyId, creditCardEnabled, achEnabled]);

  useEffect(() => {
    const updateError = () => {
      if (error) {
        if (
          paymentFormik?.values?.date &&
          (paymentFormik?.values?.method === "ACH bank payment" ||
            paymentFormik?.values?.method === "Credit/debit card") &&
          paymentFormik?.values?.billing_id
        ) {
          setError("");
        } else {
          if (paymentFormik?.values?.method === "Credit/debit card") {
            setError("Please select card");
          } else if (paymentFormik?.values?.method === "ACH bank payment") {
            setError("Please select ACH account");
          } else {
            setError("");
          }
        }
      }
    };
    updateError();
  }, [paymentFormik]);

  const [rows, setRows] = useState([
    { id: 1, account: "Select", amount: "", balance: "", dropdownOpen: false },
  ]);

  const toggleDropdown = (id) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, dropdownOpen: !row.dropdownOpen } : row
      )
    );
  };

  const handleSelect = (id, item) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id === id ? { ...row, account: item, dropdownOpen: false } : row
      )
    );
  };

  const handleAmountChange = (id, value) => {
    setRows((prevRows) =>
      prevRows.map((row) => (row.id === id ? { ...row, amount: value } : row))
    );
  };

  const totalAmount = rows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0
  );

  const handleAddRow = () => {
    const newRow = {
      id: rows.length + 1,
      account: "Select",
      amount: "",
      balance: "",
      dropdownOpen: false,
    };
    setRows([...rows, newRow]);
  };

  const handleDeleteRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };
  return (
    <>
      {loader ? (
        <Grid
          className="d-flex flex-direction-row justify-content-center align-items-center p-5 m-5"
          style={{ height: "80vh", marginTop: "25%" }}
        >
          <LoaderComponent variant="skeleton" loader={loader} height="50" width="50" />
        </Grid>
      ) : (
        <Grid
          className={`d-flex flex-column flex-lg-row w-auto gap-2 mx-2 paymentAppliedSlip ${
            invoiceData?.invoiceAccount !== "0.00"
              ? ""
              : "justify-content-center"
          }`}
        >
          {invoiceData?.invoiceAccount !== "0.00" && (
            <Card
              className="p-4 mx-3 w-100 border-blue-color leftSideMargin"
              style={{ borderRadius: "20px", border: "0.5px solid " }}
            >
              <Grid className="d-flex flex-column justify-content-between h-100 text-blue-color">
                <Grid>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: "22px",
                    }}
                  >
                    Payment applied to Invoice #{invoiceData?.InvoiceNumber}
                  </Typography>
                  <Grid className="mt-5">
                    <Grid className="d-flex gap-2">
                      <Col lg={4} md={6} sm={12} style={{ paddingTop: "8px" }}>
                        <InputDropdown
                          options={paymentMethods}
                          getOptionDisabled={getPaymentMethodDisabled}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 1 }}>
                                <span>{option || ""}</span>
                                {option === "Credit/debit card" && !creditCardEnabled && (
                                  <Typography component="span" sx={{ fontSize: "12px", color: "#B45309", fontWeight: 500, flexShrink: 0 }}>
                                    For this company, credit/debit card is disabled.
                                  </Typography>
                                )}
                                {option === "ACH bank payment" && !achEnabled && (
                                  <Typography component="span" sx={{ fontSize: "12px", color: "#B45309", fontWeight: 500, flexShrink: 0 }}>
                                    For this company, ACH is disabled.
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                          onTextFieldChange={(event, newValue) => {
                            paymentFormik.setFieldValue("method", newValue);
                            setPaymentMethod(newValue);
                          }}
                          inputValue={paymentFormik?.values?.method}
                          value={paymentFormik?.values?.method || null}
                          onChange={(event, newValue) => {
                            paymentFormik?.setFieldValue("method", newValue);
                            setPaymentMethod(newValue);
                          }}
                          getOptionLabel={(option) => option || ""}
                          error={
                            paymentFormik?.touched?.method &&
                            Boolean(paymentFormik?.errors?.method)
                          }
                          helperText={
                            paymentFormik?.touched?.method && paymentFormik?.errors?.method
                              ? paymentFormik?.errors?.method
                              : undefined
                          }
                          defaultValue="Cash"
                          name="method"
                          label="Payment Method"
                          required
                          disableClearable
                          type="text"
                          fieldHeight="44px"
                        />
                      </Col>
                      <Col lg={4} md={6} sm={12} style={{ paddingTop: "8px" }}>
                        <InputText
                          value={paymentFormik?.values?.amount}
                          onChange={paymentFormik?.handleChange}
                          onBlur={paymentFormik?.handleBlur}
                          error={
                            paymentFormik?.touched?.amount &&
                            Boolean(paymentFormik?.errors?.amount)
                          }
                          helperText={
                            paymentFormik?.touched?.amount &&
                            paymentFormik?.errors?.amount
                          }
                          name="amount"
                          label="Amount"
                          required
                          type="text"
                          placeholder="Enter Amount"
                          className="text-blue-color w-100 m-0 mb-3 paymentApplied_amout"
                          fieldHeight="44px"
                          defaultValue={invoiceData?.invoiceAccount}
                        />
                      </Col>
                      <Col
                        lg={4}
                        md={6}
                        sm={12}
                        className="mb-3 paymentApplied_paymentDate paymentDateBottmSetHera my-0 mt-0"
                      >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer
                            components={[
                              "DatePicker",
                              "DatePicker",
                              "DatePicker",
                            ]}
                          >
                            <DatePicker
                              label="Payment Date"
                              views={["year", "month", "day"]}
                              value={
                                paymentFormik?.values?.date
                                  ? dayjs(paymentFormik?.values?.date)
                                  : null
                              }
                              onChange={(value) =>
                                paymentFormik?.setFieldValue(
                                  "date",
                                  value
                                    ? dayjs(value).format("MM/DD/YYYY")
                                    : null
                                )
                              }
                              name="date"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderColor: "#0f2b46",
                                  borderRadius: "8px",
                                  "& fieldset": {
                                    borderColor: "#0f2b46",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#0f2b46",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#0f2b46",
                                  },
                                },
                              }}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                      </Col>
                    </Grid>
                    {paymentMethod === "Credit/debit card" && (
                      <CardPayment
                        paymentFormik={paymentFormik}
                        selectedCard={selectedCard}
                        setSelectedCard={setSelectedCard}
                        CustomerId={invoiceData?.CustomerId}
                        CompanyId={companyData?.CompanyId}
                        tokenDecode={{ Gateway: gateway || companyData?.Gateway, CompanyId: companyData?.CompanyId }}
                      />
                    )}
                    {paymentMethod === "ACH bank payment" && (
                      <AchPayment
                        paymentFormik={paymentFormik}
                        selectedAccount={selectedCard}
                        setSelectedAccount={setSelectedCard}
                        companyData={companyData}
                        CustomerId={invoiceData?.CustomerId}
                        tokenDecode={{ Gateway: gateway || companyData?.Gateway, CompanyId: companyData?.CompanyId }}
                      />
                    )}
                    {paymentMethod === "Check" && (
                      <InputText
                        value={paymentFormik?.values?.cheque_number}
                        onChange={paymentFormik?.handleChange}
                        onBlur={paymentFormik?.handleBlur}
                        error={
                          paymentFormik?.touched?.cheque_number &&
                          Boolean(paymentFormik?.errors?.cheque_number)
                        }
                        helperText={
                          paymentFormik?.touched?.cheque_number &&
                          paymentFormik?.errors?.cheque_number
                        }
                        name="cheque_number"
                        label="Check Number"
                        type="text"
                        className="text-blue-color w-100 m-0 mb-3 accountInformationInputTop checkNumberInputPayment chechNumberDropDOwn"
                        fieldHeight="44px"
                      />
                    )}
                    {error && <Grid style={{ color: "red" }}>{error}</Grid>}
                  </Grid>
                </Grid>
                <Grid>
                  <Row className="mt-3">
                    <Col lg="12">
                      <FormGroup>
                        <label
                          className="form-control-label fontstylerentr titleecolor fontfamilysty"
                          style={{ fontWeight: "500", fontSize: "16px" }}
                        >
                          Apply Payment to Balances
                        </label>
                        <br />
                        <label
                          className="form-control-label fontstylerentr titleecolor fontfamilysty"
                          style={{ fontWeight: "500", fontSize: "16px" }}
                        >
                          Current Balance:
                        </label>
                        <div className="table-responsive mt-3">
                          <Table
                            className="table table-bordered"
                            style={{
                              borderCollapse: "collapse",
                              border: "1px solid #152B51",
                            }}
                          >
                            <thead>
                              <tr
                                className="fontstylerentr textcolorblue fontfamilysty"
                                style={{ fontSize: "16px", fontWeight: "500" }}
                              >
                                <th>Account</th>
                                <th>Amount</th>
                                <th>Balance</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    <Dropdown
                                      isOpen={row.dropdownOpen}
                                      toggle={() => toggleDropdown(row.id)}
                                    >
                                      <DropdownToggle
                                        caret
                                        style={{
                                          width: "100%",
                                          backgroundColor: "transparent",
                                          height: "50px",
                                          borderRadius: "4px",
                                        }}
                                        className="text-blue-color"
                                      >
                                        {row.account}
                                      </DropdownToggle>
                                      <DropdownMenu
                                        style={{
                                          zIndex: 999,
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <DropdownItem
                                          className="text-blue-color"
                                          onClick={() =>
                                            handleSelect(
                                              row.id,
                                              "Add new account"
                                            )
                                          }
                                        >
                                          Add new account
                                        </DropdownItem>
                                        <DropdownItem
                                          className="text-blue-color"
                                          onClick={() =>
                                            handleSelect(row.id, "Hey")
                                          }
                                        >
                                          Hey
                                        </DropdownItem>
                                      </DropdownMenu>
                                    </Dropdown>
                                  </td>
                                  <td>
                                    <InputText
                                      label={null}
                                      ariaLabel="Amount"
                                      className="mb-3 border-blue-color text-blue-color"
                                      fieldHeight="46px"
                                      type="number"
                                      placeholder="Enter Amount"
                                      value={row.amount}
                                      onChange={(e) =>
                                        handleAmountChange(
                                          row.id,
                                          e.target.value
                                        )
                                      }
                                      fullWidth
                                    />
                                  </td>
                                  <td>
                                    <InputText
                                      label={null}
                                      ariaLabel="Balance"
                                      className="mb-3 border-blue-color text-blue-color"
                                      fieldHeight="46px"
                                      type="number"
                                      placeholder="$0.00"
                                      value={row.balance}
                                      disable
                                      fullWidth
                                    />
                                  </td>
                                  <td>
                                    <CloseIcon
                                      onClick={() => handleDeleteRow(row.id)}
                                      style={{
                                        cursor: "pointer",
                                        color: "red",
                                      }}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th
                                  className="fontstylerentmodal textcolorblue fontfamilysty"
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "500",
                                    border: "1px solid #152B51",
                                  }}
                                >
                                  Total
                                </th>
                                <th
                                  className="fontstylerentmodal textcolorblue fontfamilysty"
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "500",
                                    border: "1px solid #152B51",
                                  }}
                                >
                                  $ {totalAmount.toFixed(2)}
                                </th>
                                <th colSpan="2"></th>
                              </tr>
                              <tr>
                                <td colSpan="4">
                                  <WhiteButton
                                    className="fontstylerentmodal fontfamilysty"
                                    label="Add Row"
                                    onClick={handleAddRow}
                                    style={{
                                      border: "0.5px solid #152B51",
                                      fontSize: "16px",
                                      fontWeight: "500",
                                    }}
                                  />
                                </td>
                              </tr>
                            </tfoot>
                          </Table>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Grid>
                <Grid className="d-flex justify-content-between align-items-end mt-4 cancel_emailReceipt_save">
                  <Grid className="cancelinvoiceGrid">
                    <WhiteButton
                      className="cancelBtnInVoicce eui"
                      onClick={() => navigate(-1)}
                      label={
                        <>
                          <Typography className="text-blue-color full-sentence ">
                            Cancel
                          </Typography>
                        </>
                      }
                    />
                  </Grid>
                  {buttonLoader ? (
                    <Grid className="d-flex justify-content-end">
                      <CircularProgress size={30} color="inherit" />
                    </Grid>
                  ) : (
                    <Grid className="d-flex gap-3 saveEmail_savePay">
                      <BlueButton
                        onClick={paymentFormik?.handleSubmit}
                        label={
                          <Typography className="full-sentence">
                            Save
                          </Typography>
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Card>
          )}
        </Grid>
      )}
    </>
  );
};

export default InvoicePayment;
